import { createReducer } from '@common/redux-utils';
import {
  PropertyManagerAction,
  PropertyManagerActionType,
  GetPropertyManagerSuccessAction,
  GetPropertyManagerTypesStartAction,
  GetPropertyManagerTypesSuccessAction,
  GetPropertyManagerTypesFailureAction,
  GetPropertyManagerStatusesStartAction,
  GetPropertyManagerStatusesSuccessAction,
  GetPropertyManagerStatusesFailureAction,
  SearchPropertyManagerStartAction,
  SearchPropertyManagerSuccessAction,
  SearchPropertyManagerFailureAction,
  GetPropertyManagerContractsStartAction,
  GetPropertyManagerContractsSuccessAction,
  GetPropertyManagerContractsFailureAction,
  GetPropertyManagerDetailStartAction,
  GetPropertyManagerDetailSuccessAction,
  GetPropertyManagerDetailFailureAction
  // PropertyManagerToggleViewOnlyAction,
} from './property-manager.actions';
import { OptionsViewModel, PageViewModel } from '@models/common.models';
import { PropertyManagerViewModel, PropertyManagerSearchViewModel } from '@models/PropertyManager';
import { ContractSimpleViewModel } from '@models/Contract';
// import { PropertyManagementOfficeViewModel } from '@services/PropertyManagerService';
// import { PropertyManagerSetOfficeAction, SearchPropertyManagerStartAction, SearchPropertyManagerFailureAction } from './property-manager.actions';

export interface PropertyManagerState {
  readonly errorMessage?: string;
  readonly saving?: boolean;
  readonly statusMessage?: string;
  readonly id?: number;
  readonly isViewOnly: boolean;
  readonly viewOnlyContracts?: PageViewModel<ContractSimpleViewModel>;

  readonly query?: string | null;
  readonly propertyManagerLoading?: boolean;
  readonly propertyManagerPageCount?: number;
  readonly propertyManagerPageNumber?: number;
  readonly propertyManagerPageSize: number;
  readonly propertyManagerOrderBy?: string;
  readonly propertyManagers: PropertyManagerSearchViewModel[];
  readonly error?: string;

  readonly propertyManagerTypes: Array<OptionsViewModel>;
  readonly propertyManagerStatuses: Array<OptionsViewModel>;
  readonly loadingPropertyManagerIds: number[];
  readonly loadedComponentPropertyManager?: PropertyManagerViewModel;
  readonly propertyManagerVM: PropertyManagerViewModel | null;
  readonly contracts: Array<ContractSimpleViewModel>;
  // readonly loadedPropertyManagementOffice?: PropertyManagementOfficeViewModel;
}

// This is the initial state of the page
const initialState: PropertyManagerState = {
  errorMessage: '',
  isViewOnly: true,
  propertyManagerTypes: [],
  propertyManagerStatuses: [],
  loadingPropertyManagerIds: [],
  propertyManagers: [],
  propertyManagerPageSize: 10,
  propertyManagerVM: null,
  contracts: []
};

/********** Create **********/
function handleCreatePropertyManagerStart(
  state: PropertyManagerState,
  action: PropertyManagerAction
): PropertyManagerState {
  return {
    ...state,
    saving: true
  };
}
function handleCreatePropertyManagerSuccess(
  state: PropertyManagerState,
  action: PropertyManagerAction
): PropertyManagerState {
  return {
    ...state,
    saving: false,
    id: action.createdID
  };
}
function handleCreatePropertyManagerFailure(
  state: PropertyManagerState,
  action: PropertyManagerAction
): PropertyManagerState {
  return {
    ...state,
    saving: false,
    errorMessage: action.errorMessage || ''
  };
}

/********** Edit **********/
function handleEditPropertyManagerStart(
  state: PropertyManagerState,
  action: PropertyManagerAction
): PropertyManagerState {
  return {
    ...state,
    saving: true
  };
}
function handleEditPropertyManagerSuccess(
  state: PropertyManagerState,
  action: PropertyManagerAction
): PropertyManagerState {
  return {
    ...state,
    errorMessage: 'SUCCESS - property manager saved - ID: ' + action.createdID,
    saving: false
  };
}
function handleEditPropertyManagerFailure(
  state: PropertyManagerState,
  action: PropertyManagerAction
): PropertyManagerState {
  return {
    ...state,
    errorMessage: action.errorMessage || '',
    saving: false
  };
}

/********** Get **********/
function handleGetPropertyManagerSuccess(
  state: PropertyManagerState,
  action: GetPropertyManagerSuccessAction
): PropertyManagerState {
  return {
    ...state,
    loadedComponentPropertyManager: action.loadedPropertyManager
  };
}

/********** Get Details **********/
function handleGetPropertyManagerDetailStart(
  state: PropertyManagerState,
  action: GetPropertyManagerDetailStartAction
): PropertyManagerState {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage
  };
}
function handleGetPropertyManagerDetailSuccess(
  state: PropertyManagerState,
  action: GetPropertyManagerDetailSuccessAction
): PropertyManagerState {
  return {
    ...state,
    propertyManagerVM: action.propertyManagerVM,
    statusMessage: action.statusMessage
  };
}
function handleGetPropertyManagerDetailFailure(
  state: PropertyManagerState,
  action: GetPropertyManagerDetailFailureAction
): PropertyManagerState {
  return {
    ...state,
    errorMessage: action.errorMessage || ''
  };
}

/********** Get Contracts Via PM **********/
function handleGetPropertyManagerContractsStart(
  state: PropertyManagerState,
  action: GetPropertyManagerContractsStartAction
) {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage
  };
}
function handleGetPropertyManagerContractsSuccess(
  state: PropertyManagerState,
  action: GetPropertyManagerContractsSuccessAction
) {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage,
    contracts: action.result.results
  };
}
function handleGetPropertyManagerContractsFailure(
  state: PropertyManagerState,
  action: GetPropertyManagerContractsFailureAction
) {
  return {
    ...state,
    errorMessage: action.errorMessage,
    statusMessage: action.statusMessage
  };
}

/********** Get PM Types **********/
function handleGetPropertyManagerTypesStart(state: PropertyManagerState, action: GetPropertyManagerTypesStartAction) {
  return {
    ...state,
    message: action.message
  };
}
function handleGetPropertyManagerTypesSuccess(
  state: PropertyManagerState,
  action: GetPropertyManagerTypesSuccessAction
) {
  return {
    ...state,
    message: action.message,
    propertyManagerTypes: action.propertyManagerTypes
  };
}
function handleGetPropertyManagerTypesFailure(
  state: PropertyManagerState,
  action: GetPropertyManagerTypesFailureAction
) {
  return {
    ...state,
    message: action.errorMessage
  };
}

/********** Get PM Statuses **********/
function handleGetPropertyManagerStatusesStart(
  state: PropertyManagerState,
  action: GetPropertyManagerStatusesStartAction
) {
  return {
    ...state,
    message: action.message
  };
}
function handleGetPropertyManagerStatusesSuccess(
  state: PropertyManagerState,
  action: GetPropertyManagerStatusesSuccessAction
) {
  return {
    ...state,
    message: action.message,
    propertyManagerStatuses: action.propertyManagerStatuses
  };
}
function handleGetPropertyManagerStatusesFailure(
  state: PropertyManagerState,
  action: GetPropertyManagerStatusesFailureAction
) {
  return {
    ...state,
    message: action.errorMessage
  };
}

/********** Search For A PM **********/
function handleSearchPropertyManagerStart(
  state: PropertyManagerState,
  action: SearchPropertyManagerStartAction
): PropertyManagerState {
  return {
    ...state,
    query: action.query,
    propertyManagerLoading: true
  };
}

function handleSearchPropertyManagerSuccess(
  state: PropertyManagerState,
  action: SearchPropertyManagerSuccessAction
): PropertyManagerState {
  return {
    ...state,
    propertyManagerLoading: false,
    propertyManagerPageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    propertyManagerPageNumber: action.pageNumber,
    propertyManagerPageSize: action.pageSize,
    propertyManagerOrderBy: action.orderBy,
    propertyManagers: action.result.results
  };
}
function handleSearchPropertyManagerFailure(
  state: PropertyManagerState,
  action: SearchPropertyManagerFailureAction
): PropertyManagerState {
  return {
    ...state,
    error: action.error,
    propertyManagerLoading: false
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const PropertyManagerReducer = createReducer(initialState, {
  /********** Create **********/
  [PropertyManagerActionType.CreatePropertyManagerStart]: handleCreatePropertyManagerStart,
  [PropertyManagerActionType.CreatePropertyManagerSuccess]: handleCreatePropertyManagerSuccess,
  [PropertyManagerActionType.CreatePropertyManagerFailure]: handleCreatePropertyManagerFailure,

  /********** Edit **********/
  [PropertyManagerActionType.EditPropertyManagerStart]: handleEditPropertyManagerStart,
  [PropertyManagerActionType.EditPropertyManagerSuccess]: handleEditPropertyManagerSuccess,
  [PropertyManagerActionType.EditPropertyManagerFailure]: handleEditPropertyManagerFailure,

  /********** Get **********/
  [PropertyManagerActionType.GetPropertyManagerSuccess]: handleGetPropertyManagerSuccess,

  /********** Get Details **********/
  [PropertyManagerActionType.GetPropertyManagerDetailStart]: handleGetPropertyManagerDetailStart,
  [PropertyManagerActionType.GetPropertyManagerDetailSuccess]: handleGetPropertyManagerDetailSuccess,
  [PropertyManagerActionType.GetPropertyManagerDetailFailure]: handleGetPropertyManagerDetailFailure,

  /********** Get Contract Via PM **********/
  [PropertyManagerActionType.GetPropertyManagerContractsStart]: handleGetPropertyManagerContractsStart,
  [PropertyManagerActionType.GetPropertyManagerContractsSuccess]: handleGetPropertyManagerContractsSuccess,
  [PropertyManagerActionType.GetPropertyManagerContractsFailure]: handleGetPropertyManagerContractsFailure,

  /********** Get PM Type **********/
  [PropertyManagerActionType.GetPropertyManagerTypesStart]: handleGetPropertyManagerTypesStart,
  [PropertyManagerActionType.GetPropertyManagerTypesSuccess]: handleGetPropertyManagerTypesSuccess,
  [PropertyManagerActionType.GetPropertyManagerTypesFailure]: handleGetPropertyManagerTypesFailure,

  /********** Get PM Status **********/
  [PropertyManagerActionType.GetPropertyManagerStatusesStart]: handleGetPropertyManagerStatusesStart,
  [PropertyManagerActionType.GetPropertyManagerStatusesSuccess]: handleGetPropertyManagerStatusesSuccess,
  [PropertyManagerActionType.GetPropertyManagerStatusesFailure]: handleGetPropertyManagerStatusesFailure,

  /********** Search For A PM **********/
  [PropertyManagerActionType.SearchPropertyManagerStart]: handleSearchPropertyManagerStart,
  [PropertyManagerActionType.SearchPropertyManagerSuccess]: handleSearchPropertyManagerSuccess,
  [PropertyManagerActionType.SearchPropertyManagerFailure]: handleSearchPropertyManagerFailure
});



// WEBPACK FOOTER //
// ./src/property-manager/property-manager.reducer.ts